/**
 * Hooks React Query des phases de génération V2 (flow tuteur).
 *
 * Chaque hook : appelle la phase (edge fn) → applique les ops via
 * `exercise-blocks-mutate` → invalide le cache V2 pour rafraîchir la preview.
 * Le wizard orchestre l'enchaînement (« Tout enchaîner ») en appelant les
 * `mutateAsync` dans l'ordre.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { exerciseV2QueryKey } from './api';
import {
  runCoherencePhase,
  runSolutionPhase,
  runDictationPhase,
  runDictationEditCorrection,
  runDictateFigure,
  runAuditPhase,
  runDistractorsPhase,
  runImagePhase,
  runClueHuntPhase,
  applyBlockOps,
  type CoherencePhaseResult,
  type AuditPhaseResult,
  type PhaseOpsResult,
} from './generation-client';

export function useCoherencePhase(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      rawStatement: string;
      customInstructions?: string;
    }): Promise<CoherencePhaseResult> => {
      const res = await runCoherencePhase(exerciseId, args.rawStatement, args.customInstructions);
      await applyBlockOps(exerciseId, res.ops);
      return res;
    },
    // onSettled (et non onSuccess) : on relit les blocs même si l'application
    // échoue en cours de route, pour que l'éditeur reflète ce qui a réellement
    // été écrit (sinon « ça a marché mais ne s'affiche pas » → reload requis).
    onSettled: () => qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) }),
  });
}

export function useSolutionPhase(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: { customInstructions?: string; revisionFeedback?: string; onlyMissing?: boolean } = {},
    ): Promise<PhaseOpsResult> => {
      const res = await runSolutionPhase(exerciseId, args);
      await applyBlockOps(exerciseId, res.ops);
      return res;
    },
    // onSettled (et non onSuccess) : on relit les blocs même si l'application
    // échoue en cours de route, pour que l'éditeur reflète ce qui a réellement
    // été écrit (sinon « ça a marché mais ne s'affiche pas » → reload requis).
    onSettled: () => qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) }),
  });
}

export function useDictationPhase(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      transcript: string;
      customInstructions?: string;
      onlyMissing?: boolean;
      questionIndex?: number;
    }): Promise<PhaseOpsResult> => {
      const res = await runDictationPhase(exerciseId, args);
      await applyBlockOps(exerciseId, res.ops);
      return res;
    },
    // onSettled (et non onSuccess) : on relit les blocs même si l'application
    // échoue en cours de route, pour que l'éditeur reflète ce qui a réellement
    // été écrit (sinon « ça a marché mais ne s'affiche pas » → reload requis).
    onSettled: () => qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) }),
  });
}

/** Édition vocale d'un bloc correction (patch ciblé). */
export function useDictationEditCorrection(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      correctionBlockId: string;
      transcript: string;
      customInstructions?: string;
    }): Promise<PhaseOpsResult> => {
      const res = await runDictationEditCorrection(exerciseId, args);
      await applyBlockOps(exerciseId, res.ops);
      return res;
    },
    // onSettled (et non onSuccess) : on relit les blocs même si l'application
    // échoue en cours de route, pour que l'éditeur reflète ce qui a réellement
    // été écrit (sinon « ça a marché mais ne s'affiche pas » → reload requis).
    onSettled: () => qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) }),
  });
}

/**
 * Chasse aux indices : génère un bloc `clue_hunt` pour UNE question (index
 * ciblé ou 1re éligible). Si un `clue_hunt` existait déjà pour cette question
 * (`existing_clue_hunt_id` non null), on le SUPPRIME avant d'appliquer l'insert
 * — la mutation reste atomique côté serveur (`apply_exercise_block_ops`).
 */
export function useClueHuntPhase(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: { questionIndex?: number; customInstructions?: string } = {},
    ): Promise<
      PhaseOpsResult & { existing_clue_hunt_id: string | null; no_clues_emitted: boolean }
    > => {
      const res = await runClueHuntPhase(exerciseId, args);
      const ops = res.existing_clue_hunt_id
        ? [{ op: 'delete' as const, id: res.existing_clue_hunt_id }, ...res.ops]
        : res.ops;
      await applyBlockOps(exerciseId, ops);
      return res;
    },
    // onSettled (et non onSuccess) : on relit les blocs même si l'application
    // échoue en cours de route, pour que l'éditeur reflète ce qui a réellement
    // été écrit (sinon « ça a marché mais ne s'affiche pas » → reload requis).
    onSettled: () => qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) }),
  });
}

/** Figure dictée : génère un visual_block et l'attache au bloc cible. */
export function useDictateFigure(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      targetBlockId: string;
      transcript: string;
      customInstructions?: string;
    }): Promise<PhaseOpsResult> => {
      const res = await runDictateFigure(exerciseId, args);
      await applyBlockOps(exerciseId, res.ops);
      return res;
    },
    // onSettled (et non onSuccess) : on relit les blocs même si l'application
    // échoue en cours de route, pour que l'éditeur reflète ce qui a réellement
    // été écrit (sinon « ça a marché mais ne s'affiche pas » → reload requis).
    onSettled: () => qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) }),
  });
}

export function useAuditPhase(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<AuditPhaseResult> => {
      const res = await runAuditPhase(exerciseId);
      await applyBlockOps(exerciseId, res.ops);
      return res;
    },
    // onSettled (et non onSuccess) : on relit les blocs même si l'application
    // échoue en cours de route, pour que l'éditeur reflète ce qui a réellement
    // été écrit (sinon « ça a marché mais ne s'affiche pas » → reload requis).
    onSettled: () => qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) }),
  });
}

export function useDistractorsPhase(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: { questionIndex?: number; onlyMissing?: boolean } = {},
    ): Promise<PhaseOpsResult> => {
      const res = await runDistractorsPhase(exerciseId, args);
      await applyBlockOps(exerciseId, res.ops);
      return res;
    },
    // onSettled (et non onSuccess) : on relit les blocs même si l'application
    // échoue en cours de route, pour que l'éditeur reflète ce qui a réellement
    // été écrit (sinon « ça a marché mais ne s'affiche pas » → reload requis).
    onSettled: () => qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) }),
  });
}

export function useImagePhase(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meta: { subject: string; level: string; title: string }) =>
      runImagePhase(exerciseId, meta),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) });
      qc.invalidateQueries({ queryKey: ['exercise-detail', exerciseId] });
    },
  });
}
